import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Divider,
  Dialog,
  IconButton
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthProfile } from '../../app/hooks';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';

interface TheoryPreviewProps {
  imageUrl?: string;
  pdfUrl?: string;
  pdfName?: string;
}

export const TheoryPreview: React.FC<TheoryPreviewProps> = ({
  imageUrl = "/map/preview/theory/image.jpg",
  pdfUrl = "/map/preview/theory/Multiple Myeloma Diagnosis Theory Analysis.pdf",
  pdfName = "Multiple Myeloma Diagnosis Theory Analysis.pdf"
}) => {
  const { authStatus, isSuccess: isSuccessLoggedInUserProfile } = useAuthProfile()
  const [params] = useSearchParams();
  const t = params.get("t");
  const navigate = useNavigate()
  const [openImage, setOpenImage] = useState(false);

  function goToResponses() {
    const signInParams = new URLSearchParams({ ...t ? { t } : {}, redirect: '/conversations/?conversationId=1' });
    navigate(`/Conversations?${signInParams.toString()}`)
  }

  return (
    <Box
      id='theory-root-box'
      sx={{
        width: '100%',
        minHeight: { xs: '100%', md: '100%' },
        overflow: { xs: 'auto', md: 'auto' },
        bgcolor: '#fafafa'
      }}
    >
      <Stack>
        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 700, px: 2, pt: 3, mb: 0.5 }}>
          Multiple Myeloma Diagnosis Theory Analysis
        </Typography>
        <Typography variant="h8" sx={{ textAlign: 'center', px: 5, mb: { md: 1, lg: 2 }, fontWeight: '700', color: '#919191ff' }}>
          Comprehensive analysis and insights on Multiple Myeloma diagnosis
        </Typography>
      </Stack>
      <Divider orientation="horizontal" flexItem />

      <Box
        sx={{
          width: '100%',
          flexGrow: 1,
          backgroundColor: 'white',
          p: { xs: 0.5, md: 0, xl: 0 },
          overflowY: 'auto'
        }}
      >
        <Paper
          elevation={0}
          sx={{
            px: { xs: 1, md: 4, xl: 4 },
            pt: { xs: 1.5, md: 4, xl: 4 },
            pb: { xs: 1.5, md: 4, xl: 4 },
            mt: { xs: 0, md: 0, xl: 3 },
            borderRadius: 3,
            background: 'linear-gradient(135deg, #E5D4F5 0%, #F7DEE8 100%)',
            maxWidth: '1200px',
            margin: '0 auto'
          }}
        >
          <Stack spacing={0.5} alignItems="center">
            {/* Image */}
            <Box
              component="img"
              src={imageUrl}
              alt="Multiple Myeloma Diagnosis Theory Analysis"
              onClick={() => setOpenImage(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setOpenImage(true);
              }}
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: '500px' },
                height: 'auto',
                display: 'block',
                borderRadius: 2,
                cursor: 'zoom-in',
                '&:hover': { opacity: 0.95 }
              }}
            />

            <Dialog
              open={openImage}
              onClose={() => setOpenImage(false)}
              fullScreen
              PaperProps={{
                sx: {
                  bgcolor: 'rgba(0,0,0,0.92)',
                  m: 0,
                },
              }}
            >
              <IconButton
                onClick={() => setOpenImage(false)}
                aria-label="Close"
                sx={{
                  position: 'fixed',
                  top: 12,
                  right: 12,
                  zIndex: 2,
                  color: 'white',
                }}
              >
                <CloseIcon />
              </IconButton>

              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: { xs: 1, md: 3 },
                  overflow: 'auto',
                }}
                onClick={() => setOpenImage(false)}
              >
                <Box
                  component="img"
                  src={imageUrl}
                  alt="Multiple Myeloma Diagnosis Theory Analysis full screen"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    cursor: 'zoom-out',
                    borderRadius: 2,
                  }}
                />
              </Box>
            </Dialog>

            {/* Responses Link */}
            <Button onClick={goToResponses} sx={{ mt: -1.5, fontSize: '0.85rem', textTransform: 'none', textDecoration: 'underline' }}>
              Click to check out community responses here
            </Button>

            {/* PDF Download Section */}
            <Stack spacing={2} alignItems="center">
              <Typography variant="body1" sx={{ fontWeight: 600, textAlign: 'center' }}>
                Download the Complete Analysis
              </Typography>
              <Box
                component="a"
                href={pdfUrl}
                download={pdfName}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  textDecoration: 'none',
                  padding: '8px 32px',
                  backgroundColor: "#5468FF",
                  color: 'white',
                  borderRadius: 1,
                  fontSize: '1rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                  '&:hover': { backgroundColor: '#4353CC' }
                }}
              >
                <FileDownloadIcon sx={{ fontSize: '1.2rem' }} />
                Download PDF
              </Box>
            </Stack>
          </Stack>
        </Paper>

        {/* Footer Button */}
        <Stack
          direction="column"
          justifyContent="center"
          alignItems="center"
          spacing={2}
          sx={{ mt: { md: 1, xl: 4 }, pb: 3 }}
        >
          <Typography variant="body1" sx={{ mt: { sm: 3, md: 1, xl: 4 }, fontWeight: 600, textAlign: 'center' }}>
            {(authStatus === 'unauthenticated' && isSuccessLoggedInUserProfile) ? 'Ready to join the community?' : 'Patient Connect'}
          </Typography>
          {(authStatus === 'unauthenticated' && isSuccessLoggedInUserProfile) &&
            <Button
              id="theory-signup"
              variant="contained"
              disableElevation
              sx={{
                textTransform: 'none',
                borderRadius: 10,
                px: 6,
                py: 1,
                fontSize: '1rem',
                backgroundColor: "#5468FF",
                '&:hover': { backgroundColor: '#4353CC' }
              }}
              onClick={() => {
                const signInParams = new URLSearchParams({ ...t ? { t } : {} });
                navigate(`/SignInToMap?${signInParams.toString()}`)
              }}
            >
              Sign Up
            </Button>
          }
          <Typography variant="subtitle2" sx={{ textAlign: 'center', px: 5, mb: { md: 1, lg: 2 }, fontWeight: { sm: '400', md: '700' }, color: '#919191ff' }}>
            Whether you are newly diagnosed, in remission, preparing for treatment or supporting someone you love, Patient Connect is here to help you navigate your journey - together.
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default TheoryPreview;
